/**
 * Shell for the admin section.
 *
 * Fixed-width sidebar with brand header, nav links, and a footer with
 * help text + "Back to chat" shortcut. Main content area expands to fill
 * available space with a max-width so lists stay readable on wide screens.
 */

import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Divider from '@amzn/meridian/divider';
import Button from '@amzn/meridian/button';
import { ToastProvider } from './components/ToastProvider';

interface NavItem {
  to: string;
  label: string;
  icon: string; // emoji as icon — keeps us free of a Meridian icon import chain
  end?: boolean;
  group?: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: '◆', end: true, group: 'overview' },
  { to: '/admin/groups', label: 'Groups', icon: '◇', group: 'rbac' },
  { to: '/admin/users', label: 'Users', icon: '◉', group: 'rbac' },
  { to: '/admin/accounts', label: 'Accounts', icon: '▤', group: 'reference' },
  { to: '/admin/audit', label: 'Audit log', icon: '◐', group: 'reference' },
  { to: '/admin/bug-reports', label: 'Bug reports', icon: '⚑', group: 'reference' },
  { to: '/admin/templates', label: 'Templates', icon: '▱', group: 'config' },
  { to: '/admin/departments', label: 'Departments', icon: '◰', group: 'config' },
];

const GROUP_LABELS: Record<string, string> = {
  overview: 'Overview',
  rbac: 'RBAC',
  reference: 'Reference',
  config: 'Configuration',
};

const SIDEBAR_WIDTH = 240;
const CONTENT_MAX_WIDTH = 1440;

const linkStyle = (isActive: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '8px 12px',
  borderRadius: '6px',
  textDecoration: 'none',
  color: isActive ? '#0F1111' : '#414D5C',
  backgroundColor: isActive ? '#E6F1FA' : 'transparent',
  fontWeight: isActive ? 600 : 500,
  fontSize: '14px',
  transition: 'background-color 0.12s ease',
  borderLeft: isActive ? '3px solid #0972D3' : '3px solid transparent',
});

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();

  // Group nav items by their declared group so we can render sub-headings.
  const grouped: Array<[string, NavItem[]]> = [];
  for (const item of NAV_ITEMS) {
    const key = item.group || 'other';
    const found = grouped.find((g) => g[0] === key);
    if (found) {
      found[1].push(item);
    } else {
      grouped.push([key, [item]]);
    }
  }

  return (
    <ToastProvider>
      <div
        style={{
          display: 'flex',
          minHeight: '100vh',
          backgroundColor: '#F4F6F9',
        }}
      >
        {/* Sidebar */}
        <aside
          style={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            backgroundColor: '#FFFFFF',
            borderRight: '1px solid #E1E4E8',
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Brand */}
          <div
            style={{
              padding: '20px 20px 16px',
              borderBottom: '1px solid #E1E4E8',
            }}
          >
            <Row spacing="small" alignmentVertical="center">
              <img
                src="/ordis-logo.png"
                alt="Ordis logo"
                width={32}
                height={32}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  objectFit: 'contain',
                }}
              />
              <Column spacing="none">
                <span style={{ fontWeight: 700, fontSize: 15, color: '#0F1111' }}>Ordis Admin</span>
                <Text type="b100" color="secondary">
                  RBAC management
                </Text>
              </Column>
            </Row>
          </div>

          {/* Nav groups */}
          <nav aria-label="Admin navigation" style={{ padding: '12px 12px', flex: 1 }}>
            {grouped.map(([groupKey, items], idx) => (
              <div key={groupKey} style={{ marginBottom: idx === grouped.length - 1 ? 0 : 18 }}>
                <div
                  style={{
                    padding: '4px 12px 8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#6B7A80',
                  }}
                >
                  {GROUP_LABELS[groupKey] || groupKey}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {items.map((item) => (
                    <li key={item.to}>
                      <NavLink to={item.to} end={item.end} style={({ isActive }) => linkStyle(isActive)}>
                        <span aria-hidden="true" style={{ width: 16, fontSize: 12, opacity: 0.7 }}>
                          {item.icon}
                        </span>
                        <span>{item.label}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div
            style={{
              padding: '16px 16px 20px',
              borderTop: '1px solid #E1E4E8',
              backgroundColor: '#FAFBFC',
            }}
          >
            <Column spacing="small">
              <Button type="tertiary" size="small" onClick={() => navigate('/')}>
                ← Back to chat
              </Button>
              <Text type="b100" color="secondary">
                All admin actions are audited. Changes propagate within 5 minutes.
              </Text>
            </Column>
          </div>
        </aside>

        {/* Main content */}
        <main
          style={{
            flex: 1,
            padding: '32px 40px 48px',
            minWidth: 0,
          }}
        >
          <div style={{ maxWidth: CONTENT_MAX_WIDTH, margin: '0 auto' }}>
            <Outlet />
          </div>
        </main>
      </div>
    </ToastProvider>
  );
};
